import { type ReactNode } from 'react';

import {
  hasItemsSelected,
  type ItemId,
  type Tasks,
} from '../../../workflow/schemaTypes';
import { useWorkflow } from '../../../workflow/hooks';
import { validateItemTaskData } from '../../../utils/tasks';

/**
 * Props for the Task component
 * @property {ReactNode} children - Child elements to render when task validation passes
 * @property {string} [itemId] - Optional ID of specific item to validate
 * @property {Tasks} taskId - ID of the current task
 */
export type TaskProps = {
  children: ReactNode;
  itemId?: ItemId;
  taskId: Tasks;
};

/**
 * Task component that conditionally renders children based on workflow guard validation
 *
 * Validates task guards before rendering children. Guards can be:
 * - Item-scoped: Validates specific data for individual items
 * - Non-item-scoped: Validates general workflow data
 *
 * If itemId is provided, only validates that specific item.
 * Otherwise validates all items in itemSelection.
 */
export const Task = (props: TaskProps) => {
  const { children, itemId, taskId } = props;
  const { config, currentStep, data } = useWorkflow();

  // Early return if workflow is not properly configured
  if (!config || !currentStep) {
    return null;
  }

  const thisTask = currentStep?.tasks.find((task) => task.id === taskId);
  const { guards, mode } = thisTask ?? { guards: [] };

  if (mode !== 'enabled') {
    // Disabled and implicit tasks should not render
    return null;
  }

  // Process guards if they exist
  if (guards && guards.length > 0) {
    // Return null and thus not render the Task if any guard fails validation
    if (
      !guards.every((taskId) => {
        const guardTask = currentStep.tasks.find((task) => task.id === taskId);

        if (!guardTask) return false;

        const { schema, scope } = guardTask;

        // Handle item-scoped guards
        if (scope === 'item' && data?.items) {
          if (hasItemsSelected(data)) {
            // Validate single item if itemId provided
            if (itemId) {
              return validateItemTaskData(data.items?.[itemId], schema, taskId);
            }

            // Otherwise validate all selected items
            return Object.keys(data.items).every((itemId) =>
              validateItemTaskData(data.items?.[itemId], schema, taskId),
            );
          } else {
            // Handle non-item validation
            const guardData = data?.[taskId as keyof typeof data];
            return schema.safeParse(guardData).success;
          }
        }
      })
    ) {
      return null;
    }
  }

  // Render children if all guards pass
  return children;
};
